/**
 * Les demandes d'inclusion « à traiter », du côté de l'équipe.
 *
 * Une demande est une ligne par semaine ; l'ancien écran laissait recliquer, et
 * la même semaine pouvait être demandée dix fois. L'équipe, elle, répond à un
 * enfant : ses semaines demandées, chacune une fois, avec ce qu'il faut pour
 * décider (places, déjà inscrit ailleurs ou pas, niveau d'accès). Accepter
 * inscrit l'enfant (`create_order`, comme l'inscription admin), jamais avant
 * l'ouverture des inscriptions ; refuser et classer valent pour toutes les
 * lignes de la semaine, doublons compris.
 */

import type { NiveauAcces } from './demandes';

/** Une ligne jointe arrive en objet, ou en tableau selon la relation : on prend la première. */
export const un = <T>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null);

/** Une demande telle que la page la lit, jointures comprises. */
export interface DemandeJointe {
  id: string;
  user_id: string;
  kid_id: string;
  activity_id: string;
  request_date: string;
  status: string;
  inclusion_details?: unknown;
  kid?: OuTableau<{ prenom: string; nom: string; inclusion?: OuTableau<{ niveau_acces?: NiveauAcces | null }> | null }> | null;
  parent?: OuTableau<{ prenom: string; nom: string; email: string | null; telephone: string | null }> | null;
  session?: OuTableau<{
    start_date: string;
    end_date: string;
    periode?: string | null;
    capacity?: number | null;
    current_registrations?: number | null;
    /** L'ouverture des inscriptions de la semaine (`null` : ouvertes). */
    visible_from?: string | null;
    stage?: OuTableau<{ title: string }> | null;
    center?: OuTableau<{ name: string }> | null;
  }> | null;
}
type OuTableau<T> = T | T[];

export interface SemaineATraiter {
  sessionId: string;
  stage: string;
  centre: string;
  periode: string | null;
  debut: string;
  fin: string;
  capacite: number | null;
  inscrits: number | null;
  /** L'ouverture des inscriptions (`null` : ouvertes) : pas d'inscription avant. */
  ouverture: string | null;
  /** Les lignes en attente de cette semaine pour cet enfant : une, ou ses doublons. */
  demandeIds: string[];
  premiereDemande: string;
  /** L'enfant y est déjà inscrit (souvent par l'inscription admin, quand l'écran ne marchait plus). */
  dejaInscrit: boolean;
}

export interface GroupeATraiter {
  kidId: string;
  parentId: string;
  enfant: { prenom: string; nom: string };
  parent: { prenom: string; nom: string; email: string | null; telephone: string | null };
  /** Le niveau d'accès décidé par l'équipe ; `null` : pas encore examiné. */
  niveau: NiveauAcces | null;
  /** Dans l'ordre des dates. */
  semaines: SemaineATraiter[];
  premiereDemande: string;
  /** La fiche d'inclusion de la demande la plus récente. */
  details: unknown;
}

/** La clé d'une inscription vivante : `enfant|semaine`. */
export const cleInscription = (kidId: string, sessionId: string) => `${kidId}|${sessionId}`;

/**
 * Les demandes en attente sur des semaines qui ne sont pas finies, par enfant.
 * `aujourdhui` : date ISO. Les groupes vont du plus urgent (la première semaine
 * la plus proche) au moins urgent.
 */
export function regrouperATraiter(
  demandes: readonly DemandeJointe[],
  inscrits: ReadonlySet<string>,
  aujourdhui: string,
): GroupeATraiter[] {
  const groupes = new Map<string, GroupeATraiter>();
  /* La demande la plus récente de chaque groupe : sa fiche d'inclusion est la plus à jour. */
  const dernieres = new Map<string, string>();
  for (const d of demandes) {
    const session = un(d.session);
    if (d.status !== 'pending' || !session || session.end_date < aujourdhui) continue;

    const kid = un(d.kid);
    const parent = un(d.parent);
    const cle = `${d.kid_id}|${d.user_id}`;
    let groupe = groupes.get(cle);
    if (!groupe) {
      groupe = {
        kidId: d.kid_id,
        parentId: d.user_id,
        enfant: { prenom: kid?.prenom ?? '', nom: kid?.nom ?? '' },
        parent: {
          prenom: parent?.prenom ?? '', nom: parent?.nom ?? '',
          email: parent?.email ?? null, telephone: parent?.telephone ?? null,
        },
        niveau: un(kid?.inclusion)?.niveau_acces ?? null,
        semaines: [],
        premiereDemande: d.request_date,
        details: d.inclusion_details ?? null,
      };
      groupes.set(cle, groupe);
      dernieres.set(cle, d.request_date);
    }
    if (d.request_date < groupe.premiereDemande) groupe.premiereDemande = d.request_date;
    if (d.request_date > (dernieres.get(cle) ?? '')) {
      dernieres.set(cle, d.request_date);
      groupe.details = d.inclusion_details ?? groupe.details;
    }

    const semaine = groupe.semaines.find((s) => s.sessionId === d.activity_id);
    if (semaine) {
      semaine.demandeIds.push(d.id);
      if (d.request_date < semaine.premiereDemande) semaine.premiereDemande = d.request_date;
      continue;
    }
    groupe.semaines.push({
      sessionId: d.activity_id,
      stage: un(session.stage)?.title ?? 'Stage',
      centre: un(session.center)?.name ?? '',
      periode: session.periode ?? null,
      debut: session.start_date,
      fin: session.end_date,
      capacite: session.capacity ?? null,
      inscrits: session.current_registrations ?? null,
      ouverture: session.visible_from ?? null,
      demandeIds: [d.id],
      premiereDemande: d.request_date,
      dejaInscrit: inscrits.has(cleInscription(d.kid_id, d.activity_id)),
    });
  }

  return [...groupes.values()]
    .map((g) => ({ ...g, semaines: [...g.semaines].sort((a, b) => a.debut.localeCompare(b.debut)) }))
    .sort((a, b) => a.semaines[0].debut.localeCompare(b.semaines[0].debut) || a.premiereDemande.localeCompare(b.premiereDemande));
}

/** Les inscriptions de la semaine sont ouvertes (la règle de la page des stages, `inscriptionsOuvertes`). */
export const inscriptionsOuvertesPour = (s: Pick<SemaineATraiter, 'ouverture'>, maintenant = new Date()) =>
  !s.ouverture || Number.isNaN(new Date(s.ouverture).getTime()) || new Date(s.ouverture) <= maintenant;

/** Les semaines pas encore inscrites (celles qu'un refus vise d'office). */
export const semainesNonInscrites = (g: GroupeATraiter) => g.semaines.filter((s) => !s.dejaInscrit);

/** Les semaines qu'on peut inscrire maintenant : pas déjà inscrit, inscriptions ouvertes. */
export const semainesAInscrire = (g: GroupeATraiter, maintenant = new Date()) =>
  semainesNonInscrites(g).filter((s) => inscriptionsOuvertesPour(s, maintenant));

/** Toutes les lignes de demande de ces semaines. */
export const demandesDe = (semaines: readonly SemaineATraiter[]) => semaines.flatMap((s) => s.demandeIds);
